import React, { useState } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import type { TypeFormation, CreateGroupeDto } from '@/types/group.types';
import { getNiveauLabel, TypeFormationLabels } from '@/types/group.types';
import { createGroupes, addGroupes, type FiliereForNavigation } from '@/services/groupsService';

interface Props {
  filiere: FiliereForNavigation;
  academicYearId: string;
  niveau: number;
  typeFormation: TypeFormation;
  campusId?: string;
  existingGroupCount?: number;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const CreateGroupModal: React.FC<Props> = ({
  filiere,
  academicYearId,
  niveau,
  typeFormation,
  campusId,
  existingGroupCount = 0,
  onClose,
  onSuccess,
  onError,
}) => {
  const [groupCount, setGroupCount] = useState(1);
  const [maxStudents, setMaxStudents] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasExistingGroups = existingGroupCount > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      if (hasExistingGroups) {
        // Add groups to existing ones (will rename existing groups if needed)
        await addGroupes(
          filiere.id,
          academicYearId,
          niveau,
          typeFormation,
          groupCount,
          maxStudents === '' ? undefined : maxStudents,
          campusId
        );
      } else {
        // Create new groups
        const dto: CreateGroupeDto = {
          filiereId: filiere.id,
          niveau,
          typeFormation,
          groupCount,
          maxStudents: maxStudents === '' ? undefined : maxStudents,
          campusId,
        };
        await createGroupes(filiere.id, academicYearId, dto);
      }
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate preview names
  const previewNames = (() => {
    const labels = ['1ère', '2ème', '3ème', '4ème', '5ème'];
    const base = `${labels[niveau - 1]} Année`;
    const fc = typeFormation === 'CONTINUE' ? ' FC' : '';
    const suffixes = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    const totalGroups = existingGroupCount + groupCount;

    // If only 1 group total and no existing groups, no suffix
    if (totalGroups === 1 && existingGroupCount === 0) {
      return [`${base}${fc}`];
    }

    // Show what existing groups will be renamed to + new groups
    const allNames: string[] = [];

    // Existing groups will be renamed with suffixes A, B, C...
    for (let i = 0; i < existingGroupCount; i++) {
      allNames.push(`${base}${fc} - Groupe ${suffixes[i]} (existant)`);
    }

    // New groups continue from where existing left off
    for (let i = 0; i < groupCount; i++) {
      allNames.push(`${base}${fc} - Groupe ${suffixes[existingGroupCount + i]} (nouveau)`);
    }

    return allNames.slice(0, 6); // Show max 6 in preview
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Créer des groupes
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Info */}
          <div className="p-3 bg-gray-50 rounded-lg text-sm">
            <p className="text-gray-600">
              <strong>Filière:</strong> {filiere.filiere}
            </p>
            <p className="text-gray-600">
              <strong>Niveau:</strong> {getNiveauLabel(niveau)}
            </p>
            <p className="text-gray-600">
              <strong>Type:</strong> {TypeFormationLabels[typeFormation]}
            </p>
          </div>

          {/* Group Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de groupes à créer
            </label>
            <input
              type="number"
              min="1"
              max="26"
              value={groupCount}
              onChange={(e) => setGroupCount(parseInt(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Max Students (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre maximum d'étudiants par groupe{' '}
              <span className="text-gray-400">(optionnel)</span>
            </label>
            <input
              type="number"
              min="1"
              value={maxStudents}
              onChange={(e) =>
                setMaxStudents(e.target.value === '' ? '' : parseInt(e.target.value))
              }
              placeholder="Illimité"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Info about renaming existing groups */}
          {hasExistingGroups && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>Note:</strong> Le groupe existant sera renommé avec un suffixe (A, B, C...) pour permettre l'ajout de nouveaux groupes.
            </div>
          )}

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Aperçu des noms de groupes
            </label>
            <div className="space-y-1">
              {previewNames.map((name, index) => {
                const isExisting = name.includes('(existant)');
                const isNew = name.includes('(nouveau)');
                const displayName = name.replace(' (existant)', '').replace(' (nouveau)', '');

                return (
                  <div
                    key={index}
                    className={`px-3 py-2 text-sm rounded-lg flex items-center justify-between ${
                      isExisting
                        ? 'bg-gray-100 text-gray-700 border border-gray-200'
                        : typeFormation === 'CONTINUE'
                        ? 'bg-purple-50 text-purple-900'
                        : 'bg-primary-50 text-primary-900'
                    }`}
                  >
                    <span>{displayName}</span>
                    {isExisting && (
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                        renommé
                      </span>
                    )}
                    {isNew && (
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">
                        nouveau
                      </span>
                    )}
                  </div>
                );
              })}
              {existingGroupCount + groupCount > 6 && (
                <div className="px-3 py-2 text-sm text-gray-500 italic">
                  ... et {existingGroupCount + groupCount - 6} autre(s)
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {hasExistingGroups ? 'Ajouter' : 'Créer'} {groupCount} groupe{groupCount > 1 ? 's' : ''}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupModal;
